import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import apiPath from "../../../utils/pathObj";
import { apiGet, apiPut, apiDelete } from "../../../utils/apiFetch";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import HiringFormFieldRenderer from "./HiringFormFieldRenderer";
import useToastContext from "hooks/useToastContext";
import { comma } from "postcss/lib/list";

const FIELD_TYPES = [
  "input",
  "radio",
  "checkbox",
  "date",
  "file",
  "select",
  "textEditor",
  "textarea",
  "text",
];

const HiringEdit = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const notification = useToastContext();

  const { id } = location?.state || {};
  const [formData, setFormData] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [layouts, setLayouts] = useState({});
  const [newFieldType, setNewFieldType] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [editLabelValue, setEditLabelValue] = useState("");
  const [editOptionsValue, setEditOptionsValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [targetGroup, setTargetGroup] = useState(null);

  useEffect(() => {
    if (id) {
      fetchFormData();
    } else {
      navigate("/forms-manager");
    }
  }, [id]);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
      observer.disconnect();
    };
  }, [formData]);

  // Handle drag start between groups
  const handleDragStart = (e) => {
    const element = e.currentTarget;
    const groupIndex = parseInt(element.getAttribute("data-group-index"));
    const fieldId = element.getAttribute("data-field-id");
    const fieldElement = element.closest(".react-grid-item > div");
    if (fieldElement) {
      e.dataTransfer.setDragImage(fieldElement, 0, 0);
    }
    setDraggedItem({
      groupIndex,
      fieldId,
      element,
    });

    e.dataTransfer.setData("text/plain", `${groupIndex}-${fieldId}`);
    e.dataTransfer.effectAllowed = "move";
  };

  // Handle drag over a potential target group
  const handleDragOver = (e, groupIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedItem && draggedItem.groupIndex !== groupIndex) {
      setTargetGroup(groupIndex);
    }
  };

  const handleDrop = (e, targetGroupIndex) => {
  e.preventDefault();

  // Only handle cross-group drops
  if (!draggedItem || draggedItem.groupIndex === targetGroupIndex) {
    setDraggedItem(null);
    setTargetGroup(null);
    return;
  }

  const sourceGroupIndex = draggedItem.groupIndex;
  const fieldId = draggedItem.fieldId;

  const sourceGroup = [...formData.customFields[sourceGroupIndex]];
  
  // Modified field finding logic to check both _id and id
  const fieldIndex = sourceGroup.findIndex((f) => {
    const idToCompare = f?._id?.toString() || f?.id?.toString();
    return idToCompare == fieldId.toString();
  });
  if (fieldIndex === -1) {
    setDraggedItem(null);
    setTargetGroup(null);
    return;
  }

  const fieldToMove = sourceGroup[fieldIndex];

  setFormData((prev) => {
    const newFields = [...prev.customFields];
    
    // Modified filter logic to check both _id and id
    newFields[sourceGroupIndex] = newFields[sourceGroupIndex].filter((f) => {
      const idToCompare = f._id ? f._id.toString() : f.id ? f.id.toString() : null;
      return idToCompare !== fieldId.toString();
    });

    if (!newFields[targetGroupIndex]) {
      newFields[targetGroupIndex] = [];
    }
    newFields[targetGroupIndex] = [
      ...newFields[targetGroupIndex],
      fieldToMove,
    ];

    return {
      ...prev,
      customFields: newFields.filter((group) => group.length > 0),
    };
  });

  setLayouts((prev) => {
    const newLayouts = { ...prev };

    // Remove from source group
    if (newLayouts[sourceGroupIndex]) {
      newLayouts[sourceGroupIndex] = newLayouts[sourceGroupIndex].filter(
        (l) => !l.i.startsWith(`${sourceGroupIndex}-${fieldId}`)
      );
    }

    return newLayouts;
  });

  setDraggedItem(null);
  setTargetGroup(null);
};

  // Handle drag end (cleanup)
  const handleDragEnd = () => {
    setDraggedItem(null);
    setTargetGroup(null);
  };

  const fetchFormData = async () => {
    try {
      setIsLoading(true);
      const res = await apiGet(`${apiPath.getFormsById}/${id}`);
      const form = res?.data?.results;

      const values = {};
      const layoutByGroup = {};

      form?.customFields?.forEach((group, groupIndex) => {
        layoutByGroup[groupIndex] = group.map((field, fieldIndex) => {
          if (field.layout) {
            return {
              i: `${groupIndex}-${fieldIndex}`,
              x: field.layout.x,
              y: field.layout.y,
              w: field.layout.w,
              h:
                field.type === "textEditor"
                  ? field.layout.h + 3
                  : field.layout.h,
            };
          }

          let defaultWidth = 4;
          let defaultHeight = 1;

          if (field.type === "heading") {
            defaultWidth = 12;
            defaultHeight = 2;
          } else if (field.type === "textarea") {
            defaultWidth = 12;
            defaultHeight = 4;
          } else if (field.type === "textEditor") {
            defaultWidth = 12;
            defaultHeight = 6;
          } else if (field.type === "checkbox" || field.type === "radio") {
            defaultWidth = 6;
          }

          return {
            i: `${groupIndex}-${fieldIndex}`,
            x: (fieldIndex % 2) * 4,
            y: Infinity,
            w: defaultWidth,
            h: defaultHeight,
            static: field.type === "heading",
          };
        });

        group.forEach((field, fieldIndex) => {
          const fieldId = field?.id ?? `${groupIndex}-${fieldIndex}`;
          values[fieldId] =
            field?.value || (field?.type === "checkbox" ? [] : "");
        });
      });

      const savedLayouts = localStorage.getItem(`layouts-${id}`);

      const updatedFormData = { ...form };
      updatedFormData.customFields = form.customFields.map(
        (group, groupIndex) =>
          group.map((field, fieldIndex) => ({
            ...field,
            layout: savedLayouts
              ? JSON.parse(savedLayouts)[groupIndex]?.[fieldIndex] || {
                  x: layoutByGroup[groupIndex][fieldIndex].x,
                  y: layoutByGroup[groupIndex][fieldIndex].y,
                  w: layoutByGroup[groupIndex][fieldIndex].w,
                  h: layoutByGroup[groupIndex][fieldIndex].h,
                }
              : {
                  x: layoutByGroup[groupIndex][fieldIndex].x,
                  y: layoutByGroup[groupIndex][fieldIndex].y,
                  w: layoutByGroup[groupIndex][fieldIndex].w,
                  h: layoutByGroup[groupIndex][fieldIndex].h,
                },
          }))
      );

      setFormData(updatedFormData);
      setFieldValues(values);
      setLayouts(savedLayouts ? JSON.parse(savedLayouts) : layoutByGroup);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLayoutChange = (newLayout, groupIndex) => {
    setLayouts((prevLayouts) => {
      const updatedLayouts = { ...prevLayouts, [groupIndex]: newLayout };

      if (formData?.customFields?.[groupIndex]) {
        const updatedFields = formData.customFields[groupIndex].map(
          (field, fieldIndex) => ({
            ...field,
            layout: {
              x: newLayout[fieldIndex]?.x ?? 0,
              y: newLayout[fieldIndex]?.y ?? 0,
              w: newLayout[fieldIndex]?.w ?? 4,
              h: newLayout[fieldIndex]?.h ?? 1,
            },
          })
        );

        const updatedFormData = { ...formData };
        updatedFormData.customFields[groupIndex] = updatedFields;
        setFormData(updatedFormData);
      }
      return updatedLayouts;
    });
  };

  const handleChange = (e, fieldId, type) => {
    const { value, checked, files } = e.target;

    setFieldValues((prev) => {
      if (type === "checkbox") {
        const current = prev[fieldId] || [];
        return {
          ...prev,
          [fieldId]: checked
            ? [...current, value]
            : current.filter((v) => v !== value),
        };
      }
      if (type === "file") {
        return { ...prev, [fieldId]: files[0] };
      }
      if (type === "heading" && typeof value === "object") {
        return { ...prev, [fieldId]: value };
      }
      return { ...prev, [fieldId]: value };
    });
  };

  const handleDeleteField = async (groupIndex, fieldIndex) => {
    const field = formData?.customFields?.[groupIndex]?.[fieldIndex];
    const fieldId = field?.id;
    const formId = formData?.id;

    if (fieldId && formId) {
      try {
        await apiDelete(apiPath.deleteCustomField(formId, fieldId));
      } catch (error) {
        console.error("Error deleting custom field:", error);
        return;
      }
    }

    const updatedFields = [...formData.customFields[groupIndex]];
    updatedFields.splice(fieldIndex, 1);
    const updatedFormData = { ...formData };
    updatedFormData.customFields[groupIndex] = updatedFields;
    setFormData(updatedFormData);

    const updatedLayout = [...(layouts[groupIndex] || [])].filter(
      (_, idx) => idx !== fieldIndex
    );
    setLayouts((prevLayouts) => ({
      ...prevLayouts,
      [groupIndex]: updatedLayout,
    }));

    setFieldValues((prevValues) => {
      const updatedValues = { ...prevValues };
      delete updatedValues[fieldId];
      return updatedValues;
    });
  };

  const handleAddField = () => {
    if (!newFieldType || !formData) return;

    const newFieldId = `${Date.now()}`;

    const defaultStyles = {
      container: "mb-4 flex",
      label: "text-sm font-medium mr-2",
      input: "border border-gray-300 shadow-sm p-1",
    };

    const getInitialValue = () => {
      if (newFieldType === "checkbox") return [];
      if (newFieldType === "heading") return { heading: "", subheading: "" };
      if (newFieldType === "textEditor") return "";
      return "";
    };

    const initialValue = getInitialValue();

    const newField = {
      id: newFieldId,
      label: `${newFieldType} field`,
      type: newFieldType,
      options: ["radio", "checkbox", "select"].includes(newFieldType)
        ? ["Option 1", "Option 2"]
        : [],
      mandatory: false,
      value: initialValue,
      styles: defaultStyles,
      layout: {
        x: 0,
        y: 0,
        w: ["textarea", "textEditor"].includes(newFieldType) ? 12 : 4,
        h:
          newFieldType === "heading"
            ? 2
            : newFieldType === "textarea"
            ? 4
            : newFieldType === "textEditor"
            ? 6
            : 1,
      },
    };

    const newFieldGroup = [newField];

    setFormData((prev) => {
      const newCustomFields = [...(prev.customFields || []), newFieldGroup];
      return {
        ...prev,
        customFields: newCustomFields,
      };
    });

    setFieldValues((prev) => ({
      ...prev,
      [newFieldId]: initialValue,
    }));

    setLayouts((prev) => {
      const currentLayouts = prev || {};
      const newGroupIndex = formData.customFields?.length || 0;

      return {
        ...currentLayouts,
        [newGroupIndex]: [
          {
            i: newFieldId,
            x: 0,
            y: 0,
            w: newField.layout.w,
            h: newField.layout.h,
          },
        ],
      };
    });

    setNewFieldType("");
  };

  const startEditing = (groupIndex, fieldIndex, field, type) => {
    setEditingField({ groupIndex, fieldIndex, type });
    setEditLabelValue(field.label);
    if (type === "options") {
      setEditOptionsValue(field.options?.join(", ") || "");
    }
  };

  const saveEdit = () => {
    if (!editingField) return;

    const { groupIndex, fieldIndex, type } = editingField;
    const updatedFormData = { ...formData };
    const field = updatedFormData.customFields[groupIndex][fieldIndex];

    if (type === "label") {
      field.label = editLabelValue;
    } else if (type === "options") {
      field.options = editOptionsValue
        .split(",")
        .map((opt) => opt.trim())
        .filter((opt) => opt);
    }

    setFormData(updatedFormData);
    setEditingField(null);
  };

  const handleUpdateForm = async () => {
    if (!formData || !formData._id) {
      notification.error("Form ID is required for update.");
      return;
    }

    // 1. Validate required fields
    const hasEmptyHeading = formData.customFields.some((row) =>
      row.some(
        (field) =>
          field.type === "heading" && String(field.value || "").trim() === ""
      )
    );
    if (hasEmptyHeading) {
      notification.error("All heading fields must be filled.");
      return;
    }

    // 2. Prepare FormData
    const formDataPayload = new FormData();

    // 3. Clone customFields while preserving File objects and layout data
    const clonedCustomFields = formData.customFields.map(
      (group, groupIndex) => {
        return group.map((field, fieldIndex) => {
          const fieldKey = field?._id ? field._id : `${groupIndex}-${fieldIndex}`;
          const fieldLayout = layouts[groupIndex]?.find(
            (item) =>
              item.i === fieldKey || item.i === `${groupIndex}-${fieldIndex}`
          );

          // Get the current value
          const value = fieldValues[fieldKey] || field.value;

          // Create a new field object that includes layout data
          const fieldWithLayout = {
            ...field,
            value: value,
            // Ensure layout data is included
            layout: fieldLayout
              ? {
                  w: fieldLayout.w,
                  h:
                    field.type === "textEditor"
                      ? fieldLayout.h - 3
                      : fieldLayout.h,
                  x: fieldLayout.x,
                  y: fieldLayout.y,
                }
              : undefined,
          };

          // if (field.type === 'file') {
          //   return {
          //     ...fieldWithLayout,
          //     uploadKey: `file_${fieldKey}` // Add upload reference
          //   };
          // }
          return fieldWithLayout;
        });
      }
    );

    console.log(
      "[5] Cloned customFields with uploadKeys and layouts:",
      clonedCustomFields
    );

    // 4. Process files and append to FormData
    let fileCount = 0;
    // clonedCustomFields.forEach(row => {
    //   row.forEach(field => {
    //     if (field.type === 'file' && field.value instanceof File) {
    //       console.log(`[8] Appending file ${field.uploadKey}:`, field.value);
    //       formDataPayload.append(field.uploadKey, field.value);
    //       fileCount++;
    //     }
    //   });
    // });

    console.log(`[6] Appended ${fileCount} files to FormData`);

    // 5. Prepare fields for JSON - only keep values for textEditor fields
    const fieldsForJson = clonedCustomFields.map((row) =>
      row.map((field) => {
        const cleanField = { ...field };

        // Only keep the value if the type is 'textEditor'
        if (field.type !== "textEditor") {
          delete cleanField.value;
        }

        // For file fields, always remove the value (handled separately in formData)
        // if (field.type === 'file') {
        //   delete cleanField.value;
        // }

        return cleanField;
      })
    );

    // 6. Append other form data
    formDataPayload.append("formId", formData._id);
    formDataPayload.append(
      "formName",
      fieldValues.formName || formData.formName
    );
    formDataPayload.append(
      "formType",
      fieldValues.formType || formData.formType
    );
    formDataPayload.append(
      "category",
      fieldValues.category || formData.category
    );
    formDataPayload.append("status", "active");
    formDataPayload.append("customFields", JSON.stringify(fieldsForJson));

    for (let [key, value] of formDataPayload.entries()) {
      console.log(
        key,
        value instanceof File
          ? `[File: ${value.name}, ${value.size} bytes, ${value.type}]`
          : value
      );
    }

    // 7. Submit to API
    try {
      const res = await apiPut(apiPath.updateForm, formDataPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log(`[9] Upload progress: ${percentCompleted}%`);
        },
      });

      // 8. Handle response
      if (res?.data?.success) {
        notification.success(res.data.message || "Form updated successfully!");
        navigate("/forms-manager/view");
      } else {
        notification.error(res?.data?.message || "Update failed");
      }
    } catch (err) {
      console.error("Update failed:", {
        error: err,
        response: err.response?.data,
        stack: err.stack,
      });

      notification.error(
        err.response?.data?.message ||
          "An error occurred while updating the form"
      );
    }
  };

  if (!formData) {
    return null;
  }

  return (
    <div className="bg-[#F9F9F9]">
      <div className="py-4 px-3">
        <div className="bg-white border border-[#E9EDF9] rounded-lg">
          <div ref={containerRef} className="p-6 w-full">
            <div className="mx-auto">
              <div className="flex items-center space-between mb-6 border-b pb-4 gap-8 gap-custum">
                <h1 className="text-xl font-bold">{t("Update Hiring Form")}</h1>
                <div className="flex items-center justify-center space-x-2">
                  <label htmlFor="fieldTypeSelect" className="font-semibold">
                    Add Field Type:
                  </label>
                  <select
                    value={newFieldType}
                    onChange={(e) => setNewFieldType(e.target.value)}
                    className="border border-gray-300 rounded p-1"
                  >
                    <option value="">Select Field Type</option>
                    {FIELD_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={handleAddField}
                    disabled={!newFieldType}
                    className="bg-LightBlue text-white px-3 py-1 rounded hover:bg-LightBlue"
                  >
                    Add Field
                  </button>
                </div>
              </div>
            </div>

            {formData.customFields?.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className="mb-1 relative"
                onDragOver={(e) => handleDragOver(e, groupIndex)}
                onDrop={(e) => handleDrop(e, groupIndex)}
                style={{
                  border:
                    targetGroup === groupIndex ? "1px dashed #3182ce" : "",
                  backgroundColor: targetGroup === groupIndex ? "#ebf8ff" : "",
                }}
              >
                <GridLayout
                  className="layout"
                  layout={layouts[groupIndex]}
                  cols={12}
                  rowHeight={25}
                  width={containerWidth}
                  style={{ fontSize: "12px" }}
                  margin={[10, 10]}
                  containerPadding={[10, 10]}
                  onLayoutChange={(layout) =>
                    handleLayoutChange(layout, groupIndex)
                  }
                  draggableCancel=".non-draggable, .ql-toolbar, .ql-container, .cross-group-drag-handle"
                  isBounded={false}
                  compactType={null}
                  preventCollision={false}
                  useCSSTransforms={false}
                  autoSize={true}
                  isDraggable={!isEditing}
                  isResizable={!isEditing}
                  resizeHandles={["se"]}
                >
                  {group.map((field, fieldIndex) => {
                    const key = `${groupIndex}-${fieldIndex}`;

                    const layoutItem = layouts[groupIndex]?.find(
                      (item) => item.i === key
                    ) || {
                      i: key,
                      x: 0,
                      y: 0,
                      w: field.type === "textEditor" ? 10 : 4,
                      h: field.type === "textEditor" ? 6 : 1,
                    };

                    const safeLayout = {
                      i: key,
                      x: Number.isFinite(layoutItem.x) ? layoutItem.x : 0,
                      y: Number.isFinite(layoutItem.y) ? layoutItem.y : 0,
                     w: Math.min(
                        Number.isFinite(layoutItem.w)
                          ? layoutItem.w
                          : field.type === "textEditor"
                          ? 10
                          : 4,
                        12
                      ),
                      h: Number.isFinite(layoutItem.h)
                        ? layoutItem.h
                        : field.type === "textEditor"
                        ? 6
                        : 1,
                    };

                    return (
                      <div
                        key={key}
                        data-grid={safeLayout}
                        className="rounded bg-white"
                        style={{ zIndex: 100 }}
                      >
                        <div className="bg-white">
                          <div className="flex justify-start gap-1 items-start">
                            <div
                              className="cross-group-drag-handle cursor-grab"
                              draggable
                              onDragStart={handleDragStart}
                              onDragEnd={handleDragEnd}
                              data-group-index={groupIndex}
                              data-field-id={
                                field._id || field.id || `${groupIndex}-${fieldIndex}`
                              }
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              ⠿
                            </div>
                            <div className="w-full">
                              <HiringFormFieldRenderer
                                field={field}
                                groupIndex={groupIndex}
                                fieldIndex={fieldIndex}
                                fieldValues={fieldValues}
                                editingField={editingField}
                                editLabelValue={editLabelValue}
                                editOptionsValue={editOptionsValue}
                                handleChange={handleChange}
                                handleDeleteField={handleDeleteField}
                                startEditing={startEditing}
                                saveEdit={saveEdit}
                                setEditLabelValue={setEditLabelValue}
                                setEditOptionsValue={setEditOptionsValue}
                                setEditingField={setEditingField}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </GridLayout>
              </div>
            ))}

            <div className="flex justify-end gap-4 mt-16 py-2 border-t">
              <button
                onClick={() => navigate("/forms-manager/view")}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                onClick={handleUpdateForm}
                className="px-6 py-2 bg-LightBlue text-white rounded hover:bg-LightBlue"
              >
                {t("Update Form")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HiringEdit;
